<script setup lang="ts" generic="T extends boolean | any[]">
import { computed, type PropType, useSlots } from "vue";
import type { UIColor } from "../../..";
import Icon from "../Icon/Icon.vue";
import Overlay from "../../internal/Overlay/Overlay.vue";
import CompositionSpacer from "../../molecules/Composition/CompositionSpacer.vue";

const props = defineProps({
  /*
      If bound to array, this value is the data
    */
  value: {
    type: [Boolean, Array, String, Number, Object],
    default: "",
  },
  /*
      Icon color
      values: see src/css/color.css
    */
  color: {
    type: String as PropType<UIColor>,
    required: true,
  },
  /*
      Indeterminate
    */
  indeterminate: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  iconIdTrue: {
    type: String,
    default: "check_box",
  },

  iconIdFalse: {
    type: String,
    default: "check_box_outline_blank",
  },

  alignContentTop: {
    type: Boolean,
    default: false,
  },

  hoverEffectDisabled: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();

const _value = defineModel<T>({ required: true });

const isChecked = computed(() =>
  _value.value instanceof Array
    ? _value.value.includes(props.value)
    : (_value.value as boolean)
);

const iconId = computed(() =>
  props.indeterminate
    ? "indeterminate_check_box"
    : isChecked.value
    ? props.iconIdTrue
    : props.iconIdFalse
);

const labelBeforePopulated = computed(() => slots.labelBefore !== undefined);
const labelAfterPopulated = computed(() => slots.labelAfter !== undefined);
</script>

<template>
  <div
    :class="[
      'tick-box',
      $style.container,
      (disabled || hoverEffectDisabled) && $style.disabled,
      alignContentTop && $style.alignContentTop,
    ]"
  >
    <input
      ref="checkbox"
      v-model="_value"
      :value="value"
      :checked="isChecked"
      :class="[$style.input]"
      type="checkbox"
    />

    <div
      v-if="labelBeforePopulated"
      :class="[
        $style.labelBefore,
        (disabled || hoverEffectDisabled) && $style.disabled,
      ]"
    >
      <slot name="labelBefore" />

      <CompositionSpacer direction="horizontal" size="blip" />
    </div>

    <div
      :class="[
        'tick-box-icon',
        $style.icon,
        disabled && $style.disabled,
        hoverEffectDisabled && $style.hoverEffectDisabled,
      ]"
    >
      <Overlay
        v-if="disabled"
        colorBackground="--color-primary"
        colorForeground="--color-primary"
        opacityBackground="0.075"
        opacityForeground="0.1"
      />

      <Icon :id="iconId" size="control" :color="color" />
    </div>

    <div
      v-if="labelAfterPopulated"
      :class="[
        $style.labelAfter,
        (disabled || hoverEffectDisabled) && $style.disabled,
      ]"
    >
      <CompositionSpacer direction="horizontal" size="blip" />

      <slot name="labelAfter" />
    </div>
  </div>
</template>

<style module>
.container {
  display: inline-flex;
  flex-shrink: 1;
  width: calc(100% - 2px);
  position: relative;
  align-items: center;
  margin-left: 1px;
  margin-right: 1px;
}

.container.disabled {
  pointer-events: none;
}

.alignContentTop {
  align-items: flex-start;
}

.input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  margin: 0;
  padding: 0;
  cursor: pointer;
}

.labelBefore,
.labelAfter {
  display: inline-flex;
  user-select: none;
}

.icon {
  display: flex;
  position: relative;
  transition-property: transform;
  transition-duration: var(--transition-duration-shortest);
  transition-timing-function: ease-in-out;
  clip-path: inset(0 0 0 0 round var(--border-radius-medium));
}

.labelAfter.disabled,
.labelBefore.disabled,
.icon.disabled {
  pointer-events: all;
  cursor: not-allowed;
}

:global(.tick-box:hover .tick-box-icon) {
  transform: scale(1.075);
}

.hoverEffectDisabled {
  transform: none;
}
</style>
